import { useEffect, useState, type Dispatch, type SetStateAction } from 'react';
import type { GeneralSettingsDto, UpdateGeneralSettingsInput } from '@gc/shared';

import { api } from '@/lib/api';
import { toFieldErrors, useResource, type FieldError } from './useSettings';

/**
 * The subset of the venue row one page writes.
 *
 * `Partial<...>`, not the inferred input type itself: `pointsName` carries a
 * zod `.transform`, which makes its key REQUIRED in the inferred type even
 * though the schema accepts its absence — and only the Loyalty page shows it.
 * The server writes what it is sent and leaves out what it is not, so a page
 * must be able to omit any field it does not render.
 */
export type GeneralSettingsPatch = Partial<UpdateGeneralSettingsInput>;

/**
 * The shared spine of the four General-Settings pages (Venue, Printing,
 * Portal & kiosk, Loyalty).
 *
 * WHY FOUR PAGES CAN SHARE ONE ROW: `GET /settings/general` returns the whole
 * VenueSettings record, and every field in `updateGeneralSettingsSchema` is
 * optional. The server writes only what it is sent — a field a page omits is
 * left alone, not nulled — so each page can PATCH back its own fields and
 * nothing else. That property is what made splitting the 976-line page safe,
 * and it is why each page must send ONLY the fields it actually shows: a page
 * that echoed a field it does not render would write back whatever it happened
 * to have loaded, silently reverting an edit made on another page.
 *
 * This hook exists so the four copies of "load, edit, submit, re-read" cannot
 * drift apart.
 */
export interface GeneralForm {
  /** The row as the server last returned it. A page can diff `form` against
   *  this to know whether it has unsaved edits. */
  data: GeneralSettingsDto | null;
  /** The edited copy; null until the first load lands. */
  form: GeneralSettingsDto | null;
  setForm: Dispatch<SetStateAction<GeneralSettingsDto | null>>;
  set: <K extends keyof GeneralSettingsDto>(key: K, value: GeneralSettingsDto[K]) => void;
  loading: boolean;
  /** Load failure. Save failures land in `errors`/`errorFor` instead. */
  error: string | null;
  reload: () => Promise<void>;
  errors: FieldError[];
  setErrors: Dispatch<SetStateAction<FieldError[]>>;
  errorFor: (field: string) => string | undefined;
  saving: boolean;
  saved: boolean;
  save: (payload: GeneralSettingsPatch, failedMessage: string) => Promise<boolean>;
}

export function useGeneralForm(): GeneralForm {
  const { data, loading, error, reload } = useResource<GeneralSettingsDto>('/settings/general');

  const [form, setForm] = useState<GeneralSettingsDto | null>(null);
  const [errors, setErrors] = useState<FieldError[]>([]);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (data) setForm(data);
  }, [data]);

  const set = <K extends keyof GeneralSettingsDto>(key: K, value: GeneralSettingsDto[K]): void =>
    setForm((current) => (current ? { ...current, [key]: value } : current));

  const errorFor = (field: string): string | undefined =>
    errors.find((issue) => issue.field === field)?.message;

  /**
   * PATCHes `payload` and re-reads the row.
   *
   * Returns whether it stuck, so a caller holding state the row cannot round-
   * trip — the hot-key password, which is write-only — knows when clearing its
   * input would throw away something that was never saved.
   */
  async function save(payload: GeneralSettingsPatch, failedMessage: string): Promise<boolean> {
    setErrors([]);
    setSaved(false);
    setSaving(true);
    try {
      await api.patch('/settings/general', payload);
      setSaved(true);
      await reload();
      return true;
    } catch (caught) {
      setErrors(toFieldErrors(caught, failedMessage));
      return false;
    } finally {
      setSaving(false);
    }
  }

  return {
    data,
    form,
    setForm,
    set,
    loading,
    error,
    reload,
    errors,
    setErrors,
    errorFor,
    saving,
    saved,
    save,
  };
}
